/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  datalabHttp,
  datalabWithTokenHttp,
} from '@dbl/services/common';
import { LANGUAGE_CN } from '@home/_config/config';
import { AxiosRequestConfig } from 'axios';
import { getLocale } from 'umi';

import { transformDashboardInfo } from './transformers';

/**
 * 作用：获取报表列表（工作台“我的报表”等页面使用）。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> getDashboardList
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/MyDashboard/index.tsx`
 */
export function getDashboardList(params: any) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/list',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

/**
 * 作用：发布/取消发布报表。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> publishDashboard
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/MyDashboard/index.tsx`
 * - `src/pages/datalab/pages/WorkBench/components/PublishModal/index.tsx`
 */
export function publishDashboard(params: any) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/publish',
    method: 'post',
    data: params,
    useCustomTips: true,
  };
  return datalabHttp(options);
}

/**
 * 作用：删除报表。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> deleteDashboard
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/MyDashboard/index.tsx`
 */
export function deleteDashboard(params: any) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/delete',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

/**
 * 作用：筛选器生效范围类型（报表顶部筛选器 scope）。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> ScopeType
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/Filter.tsx`
 * - `src/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/FilterScope.tsx`
 */
export enum ScopeType {
  SameDataset = 'same_dataset',
  SpecificChart = 'specific_chart',
}

/**
 * 作用：筛选器作用域中的图表描述，用于 scope 编辑与保存。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> IScopeChart
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/Filter.tsx`
 * - `src/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/FilterScope.tsx`
 */
export interface IScopeChart {
  task_id: string;
  columnDatasetId: string;
  name?: string;
  name_cn?: string;
  apply_to_column?: {
    table_id: string;
    table_code: string;
    column: string;
    column_type: string;
  }[];
  isDefaultDate?: boolean;
}

interface IAddDashboardParams {
  chart_ids: string;
  dashboard_title: string;
  description: string;
  game_code: string;
  json_metadata: string;
  version: string;
}

/**
 * 作用：保存报表（新建）。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> saveDashboard
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/CreateReport/hooks.ts`
 */
export function saveDashboard(params: IAddDashboardParams, reqCfg?: any) {
  const locale = getLocale();
  const language = locale === LANGUAGE_CN ? 'zh' : 'en';
  params.version = 'v2';
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/add',
    method: 'post',
    data: params,
    headers: {
      'accept-language': language,
    },
    ...reqCfg,
  };
  return datalabHttp(options);
}

interface IUpdateDashboardParams extends IAddDashboardParams {
  id: number;
}

/**
 * 作用：更新报表（编辑）。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> updateDashboard
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/CreateReport/hooks.ts`
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/SaveModel/index.tsx`
 */
export function updateDashboard(params: IUpdateDashboardParams, reqCfg?: any) {
  const locale = getLocale();
  const language = locale === LANGUAGE_CN ? 'zh' : 'en';
  params.version = 'v2';
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/update',
    method: 'post',
    data: params,
    headers: {
      'accept-language': language,
    },
    ...reqCfg,
  };
  return datalabHttp(options);
}

export const DashboardDefaultTabId = '-1';

/**
 * 作用：获取报表详情，并进行兼容旧数据格式的前端转换。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> getDashboard
 * 原始引用（used-by）：
 * - `src/pages/datalab/components/DashboardLayout/hooks.ts`
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/SaveModel/index.tsx`
 */
export function getDashboard(params: any) {
  params.version = 'v2';
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/info',
    method: 'post',
    data: params,
    formatData: transformDashboardInfo,
  };
  return datalabWithTokenHttp(options);
}

interface ICopyDashboardParam {
  id: string;
  name: string;
  copy_chart: 'copy' | 'refer';
  game_code: string;
  show_contact: string;
}

/**
 * 作用：复制报表。
 *
 * 原始实现：`src/pages/datalab/services/customDashboard.ts` -> copyDashboard
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/WorkBench/components/CopyDashboardModal/index.tsx`
 */
export function copyDashboard(params: ICopyDashboardParam) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/copy',
    method: 'post',
    data: params,
    useCustomTips: true,
  };
  return datalabHttp(options);
}

/**
 * 作用：获取报表列表（用于添加图表时选择报表）。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getReportList
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/SaveModel/index.tsx`
 */
export function getReportList(params: { game_code: string; }) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/custom_report/list_for_add',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

